import { Chip, IconButton, Stack, useTheme } from "@mui/material";
import { useHasMultipleConnectedAgents } from "web_myplann/Activities/Hooks/useHasMultipleConnectedAgents";
import { useAgentQuery } from "web_myplann/Api/Advisor/useAgentQuery";
import { useTaskQuery } from "web_myplann/Api/Tasks/useTaskQuery";
import { useTaskWorkflowsQuery } from "web_myplann/Api/Tasks/useTaskWorkflowsQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { Steps } from "web_myplann/Shared/Components/Steps";
import { ToolBarMenu } from "web_myplann/Shared/Components/ToolBarMenu";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useFormatUserWithType } from "web_myplann/Shared/Hooks/useFormatUserWithType";
import { useOpenAgentChat } from "web_myplann/Shared/Hooks/useOpenAgentChat";
import { getBackFallbackRoute } from "web_myplann/Shared/Utils/backNavigation";
import { dateTimeFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { getAgentChatTitle } from "web_myplann/Shared/Utils/getAgentChatTitle";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";
import { useTaskMenuItems } from "web_myplann/Tasks/Hooks/useTaskMenuItems";
import "dayjs/locale/cs";
import { ChartNoAxesGantt, Ellipsis, Info, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";

export const TaskDetailPage = () => {
	const routeParams = useParams<{ id: string }>();
	const taskId = getTypedParam(routeParams, "id", Number) ?? null;
	const theme = useTheme();
	const { t } = useResource();
	const location = useLocation();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { data: taskData, isFetching: isTaskFetching } = useTaskQuery(taskId);
	const { data: taskWorkflowsData, isFetching: isTaskWorkflowsFetching } =
		useTaskWorkflowsQuery(taskId);

	const { data: dataAgent } = useAgentQuery(taskData?.author.id);
	const hasMultipleConnectedAgents = useHasMultipleConnectedAgents();
	const formatUserWithType = useFormatUserWithType();
	const openAgentChat = useOpenAgentChat();

	const menuItems = useTaskMenuItems(dataAgent);

	const hasWorkflows = !!taskWorkflowsData?.items?.length;

	return (
		<>
			<MyPlannLayout
				topBar={
					<NavigationHeader
						title={t(Resources.Task.Detail.title)}
						defaultRoute={getBackFallbackRoute(location.pathname)}
						replaceOnBack
						endComponent={
							<Stack direction="row" gap={1.6}>
								<IconButton
									sx={{ padding: 0 }}
									onClick={() =>
										openAgentChat({
											uid: dataAgent?.uid,
											title: getAgentChatTitle(dataAgent),
											conversationId: dataAgent?.conversationId,
										})
									}
								>
									<MessageCircle
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
								<IconButton
									sx={{ padding: 0 }}
									onClick={(event) => {
										event.stopPropagation();
										setAnchorEl(event.currentTarget);
									}}
									disableFocusRipple
									disableTouchRipple
								>
									<Ellipsis
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
							</Stack>
						}
					/>
				}
			>
				{isTaskFetching || isTaskWorkflowsFetching ? (
					<DetailSectionLoader />
				) : (
					<Stack paddingTop={1.6}>
						<DataCard
							icon={
								<Info
									size={theme.iconSize.sm}
									color={theme.palette.deprecated.grey[700]}
								/>
							}
							label={t(Resources.Task.Detail.BasicInfo.title)}
							noDivider={!hasWorkflows}
						>
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Name)}
								value={taskData?.name}
							/>
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Date)}
								value={dateTimeFormatter(taskData?.dueDate)}
							/>
							{taskData?.statusName && (
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.State)}
									element={
										<Chip
											label={taskData?.statusName}
											variant="filled"
											sx={{
												fontWeight: 500,
												color: theme.palette.getContrastText(
													taskData?.statusColor ?? "#000",
												),
												backgroundColor: taskData?.statusColor,
												borderRadius: theme.shape.pillRadius,
											}}
										/>
									}
								/>
							)}
							{hasMultipleConnectedAgents && (
								<DataRow
									label={t(Resources.Task.Detail.BasicInfo.CreatedBy)}
									value={taskData ? formatUserWithType(taskData.author) : ""}
								/>
							)}
							<DataRow
								label={t(Resources.Task.Detail.BasicInfo.Solver)}
								value={
									taskData?.assignedTo
										? formatUserWithType(taskData.assignedTo)
										: ""
								}
							/>
							<DataNote
								label={t(Resources.Task.Detail.BasicInfo.Desc)}
								text={taskData?.description}
							/>
						</DataCard>

						<DataCard
							icon={
								<ChartNoAxesGantt
									size={theme.iconSize.sm}
									color={theme.palette.deprecated.grey[700]}
								/>
							}
							label={t(Resources.Task.Detail.Process.title)}
							noDivider
							isEmpty={!hasWorkflows}
						>
							<Stack gap={0.6}>
								{taskWorkflowsData?.items?.map((w) => (
									<Steps key={w.taskWorkflowID} name={w.name} steps={w.steps} />
								))}
							</Stack>
						</DataCard>
					</Stack>
				)}
			</MyPlannLayout>
			<ToolBarMenu
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				items={menuItems}
			/>
		</>
	);
};
